import type { AnyProperty } from '@/utilities.types';
import type { GroupBy } from '@/serverSideTable/serverSideTable.constants';
import type { SeeqAxiosRequestConfig } from '@/requests/axios.utilities';
import { sqGraphQLApi } from '@/sdk/api/GraphQLApi';
import type { TableData } from '@/serverSideTable/materializedTables/materializedTables.constants';
import { zipObject } from 'lodash';
import type { Event } from '@/serverSideTable/materializedTables/queries.constants';
import { GET_TABLE_QUERY } from '@/serverSideTable/materializedTables/queries.constants';

export async function graphql<T>(query: string, variables: AnyProperty, cancellationGroup?: string): Promise<T> {
  const config: SeeqAxiosRequestConfig = {};
  if (cancellationGroup) {
    config.cancellationGroup = cancellationGroup;
    config.cancelOnServer = true;
  }
  const {
    data: { errors, data },
  } = await sqGraphQLApi.graphql({ query, variables }, config);
  if (errors) {
    throw Error(JSON.stringify(errors));
  }

  return data;
}

export function publish(tableId: string, events: Event[]) {
  return graphql('mutation PublishEvents($rowEvents: RowEventsInput!) { publishEvent(rowEvents: $rowEvents) }', {
    rowEvents: { tableDefinitionId: tableId, events: events.map((event) => ({ genericEvent: event })) },
  });
}

export function tabulate({ rows, headers }: TableData): AnyProperty[] {
  const headerNames = headers.map((h) => h.name);

  return rows.map((row) => zipObject(headerNames, row));
}

export async function getTable(
  tableId: string,
  {
    limit,
    columns,
    properties,
    filter,
    sorts,
    cursor,
    useDistinct,
    virtualColumns,
    groupBy,
    conditionMonitorId,
  }: {
    limit?: number;
    columns: string[];
    properties?: AnyProperty[];
    filter?: any;
    sorts?: { columnName: string; ascending: boolean }[];
    cursor?: { cursorKey: string };
    useDistinct?: boolean;
    virtualColumns?: { vantage: string[] };
    groupBy?: GroupBy;
    conditionMonitorId?: string;
  },
  cancellationGroup?: string,
) {
  const data = await graphql<{ table: TableData }>(
    GET_TABLE_QUERY,
    {
      id: tableId,
      limit,
      columnsToInclude: columns,
      propertiesToInclude: properties,
      filter,
      sorts,
      cursor,
      useDistinct,
      virtualColumns,
      groupBy,
      conditionMonitorId,
    },
    cancellationGroup,
  );

  return { data: tabulate(data.table), cursorKey: data.table.cursorKey };
}

/**
 * Fetch distinct values for a specific column in a materialized table
 *
 * @param tableDefinitionId - The ID of the table definition for the materialized table
 * @param columnName - The name of the column to fetch distinct values for
 */
export async function fetchDistinctValuesForColumn<ColumnType = string>(
  tableDefinitionId: string,
  columnName: string,
  limit: number,
): Promise<ColumnType[]> {
  const response = await getTable(tableDefinitionId, {
    limit,
    useDistinct: true,
    columns: [columnName],
  });

  return response.data.map((item) => item[columnName]);
}
